/**
 * CalendarGrid — Monthly calendar grid for the Calendar Module.
 *
 * Displays a full month view with navigation controls, weekday headers,
 * and DayCell components showing events and tasks for each day.
 * Reads state from useCalendarStore and useTaskStore to combine
 * calendar events with tasks that have due dates.
 *
 * @see Requirements 5.1, 5.2, 5.3, 5.8, 10.5
 */

import { useMemo } from 'react';
import { useCalendarStore, useTaskStore } from '../../stores';
import type { CalendarEvent } from '../../types/calendar';
import type { Task } from '../../types/tasks';
import { DayCell } from './DayCell';
import { CalendarAccent } from '../ui/CalendarAccent';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Weekday headers starting from Monday per design. */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Format a Date to YYYY-MM-DD.
 */
function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Get today's date as YYYY-MM-DD.
 */
function getTodayStr(): string {
  return formatDate(new Date());
}

/**
 * Get number of days in a given month/year.
 */
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Get the day of week index (0=Mon, 6=Sun) for a given date.
 */
function getMondayBasedDayOfWeek(date: Date): number {
  const day = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  return day === 0 ? 6 : day - 1;
}

/**
 * Generate all the dates to display in the monthly grid,
 * including padding days from previous/next months.
 */
function generateMonthDays(year: number, month: number): { date: string; isOtherMonth: boolean }[] {
  const days: { date: string; isOtherMonth: boolean }[] = [];

  const firstOfMonth = new Date(year, month, 1);
  const startDayOfWeek = getMondayBasedDayOfWeek(firstOfMonth);
  const daysInCurrent = getDaysInMonth(year, month);

  // Padding from previous month
  if (startDayOfWeek > 0) {
    const prevMonthDays = getDaysInMonth(year, month - 1);
    const prevYear = month === 0 ? year - 1 : year;
    const prevMonth = month === 0 ? 11 : month - 1;
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      days.push({
        date: formatDate(new Date(prevYear, prevMonth, day)),
        isOtherMonth: true,
      });
    }
  }

  // Current month days
  for (let day = 1; day <= daysInCurrent; day++) {
    days.push({
      date: formatDate(new Date(year, month, day)),
      isOtherMonth: false,
    });
  }

  // Padding from next month to fill remaining cells (complete weeks)
  const totalCells = days.length <= 35 ? 35 : 42;
  const remaining = totalCells - days.length;
  const nextYear = month === 11 ? year + 1 : year;
  const nextMonth = month === 11 ? 0 : month + 1;
  for (let day = 1; day <= remaining; day++) {
    days.push({
      date: formatDate(new Date(nextYear, nextMonth, day)),
      isOtherMonth: true,
    });
  }

  return days;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * CalendarGrid renders the monthly calendar with event and task indicators.
 * Reads from useCalendarStore for events, navigation, and selected date.
 * Reads from useTaskStore for tasks with due dates to show on the grid.
 */
export function CalendarGrid() {
  const {
    selectedDate,
    events,
    navigateMonth,
    setSelectedDate,
  } = useCalendarStore();

  const { tasks } = useTaskStore();

  // Parse selected date to get year/month
  const parts = selectedDate.split('-');
  const year = parseInt(parts[0] ?? '2024', 10);
  const month = parseInt(parts[1] ?? '1', 10) - 1; // 0-indexed

  const todayStr = useMemo(() => getTodayStr(), []);

  // Generate grid days (including padding from adjacent months)
  const gridDays = useMemo(() => generateMonthDays(year, month), [year, month]);

  // Index events by date (YYYY-MM-DD) for O(1) lookup
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const event of events) {
      // Extract date portion from startDate (could be ISO datetime or date)
      const dateKey = event.startDate.split('T')[0] ?? event.startDate;
      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey]!.push(event);
    }
    return map;
  }, [events]);

  // Index non-completed tasks by dueDate for O(1) lookup
  const tasksByDate = useMemo(() => {
    const map: Record<string, Task[]> = {};
    for (const task of tasks) {
      if (task.dueDate && !task.isCompleted && !task.deleted) {
        if (!map[task.dueDate]) {
          map[task.dueDate] = [];
        }
        map[task.dueDate]!.push(task);
      }
    }
    return map;
  }, [tasks]);

  const monthLabel = `${MONTH_NAMES[month] ?? 'Unknown'} ${year}`;

  return (
    <div
      className="relative p-4 bg-bg-card rounded-xl shadow-card"
      role="region"
      aria-label="Calendar"
    >
      {/* Decorative accents */}
      <div className="absolute top-3 right-3 opacity-40 pointer-events-none" aria-hidden="true">
        <CalendarAccent variant="flower" size={18} />
      </div>
      <div className="absolute bottom-3 left-3 opacity-30 pointer-events-none" aria-hidden="true">
        <CalendarAccent variant="sparkle" size={14} />
      </div>

      {/* Navigation header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          className="p-2 rounded-lg text-text-secondary hover:bg-hover-bg hover:text-text-primary transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          aria-label="Previous month"
        >
          <ChevronLeftIcon />
        </button>

        <h2
          className="text-lg font-semibold text-text-heading select-none"
          aria-live="polite"
          aria-atomic="true"
        >
          {monthLabel}
        </h2>

        <button
          type="button"
          onClick={() => navigateMonth(1)}
          className="p-2 rounded-lg text-text-secondary hover:bg-hover-bg hover:text-text-primary transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          aria-label="Next month"
        >
          <ChevronRightIcon />
        </button>
      </div>

      {/* Weekday column headers */}
      <div className="grid grid-cols-7 mb-1" role="row">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="text-center text-xs text-text-secondary font-medium py-1"
            role="columnheader"
            aria-label={day}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div
        className="grid grid-cols-7 gap-0.5"
        role="grid"
        aria-label={`${monthLabel} calendar`}
      >
        {gridDays.map(({ date, isOtherMonth }) => (
          <DayCell
            key={date}
            date={date}
            events={eventsByDate[date] || []}
            tasks={tasksByDate[date] || []}
            isToday={date === todayStr}
            isSelected={date === selectedDate}
            isOtherMonth={isOtherMonth}
            onClick={() => setSelectedDate(date)}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
        <div className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full bg-secondary"
            aria-hidden="true"
          />
          <span className="text-xs text-text-secondary">Events</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg
            width="10"
            height="10"
            viewBox="0 0 16 16"
            fill="none"
            className="text-primary"
            aria-hidden="true"
          >
            <rect
              x="1"
              y="1"
              width="14"
              height="14"
              rx="3"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M4.5 8L7 10.5L11.5 5.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xs text-text-secondary">Tasks</span>
        </div>
      </div>
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function ChevronLeftIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
